import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { getTransferenciasEstoque, getTransferenciaRomaneioPdf } from '../services/api';
import { getAuthPermissoes } from '../services/authStorage';
import { useEstabelecimento } from '../context/EstabelecimentoContext';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import SortableTh from './common/SortableTh';
import { useClientSort } from '../hooks/useClientSort';
import { formatDateTimeLocale } from '../utils/datetimeLocal';
import { podeTransferencia, statusTransferencia } from '../utils/transferenciaEstoque';
import { getApiErrorMessage } from '../utils/apiError';

const STATUS_FILTRO = [
  { value: '', label: 'Todos' },
  { value: '0', label: 'Criado' },
  { value: '1', label: 'Separado' },
  { value: '2', label: 'Concluído' },
  { value: '3', label: 'Cancelado' },
];

export default function TransferenciaEstoqueList() {
  const navigate = useNavigate();
  const permissions = useMemo(() => getAuthPermissoes(), []);
  const { estabelecimentoId } = useEstabelecimento();

  const [page, setPage] = useState(1);
  const [termo, setTermo] = useState('');
  const [termoEmUso, setTermoEmUso] = useState('');
  const [status, setStatus] = useState('');
  const [items, setItems] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const podeConsultar = podeTransferencia(permissions, 'consultar');
  const podeCriar = podeTransferencia(permissions, 'criar');

  const carregar = useCallback(async () => {
    if (!podeConsultar) {
      setError('Sem permissão para consultar transferências.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await getTransferenciasEstoque({
        indice: page,
        qtde: 20,
        estabelecimentoId: estabelecimentoId || undefined,
        status: status === '' ? undefined : status,
        termo: termoEmUso || undefined,
      });
      setItems(data?.items || []);
      setTotalPages(data?.totalPages || 1);
      setTotalItems(data?.totalItems || 0);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar as transferências.'));
    } finally {
      setLoading(false);
    }
  }, [page, termoEmUso, status, estabelecimentoId, podeConsultar]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    setPage(1);
  }, [status, estabelecimentoId]);

  const { sorted: itemsOrdenados, orderBy, order, requestSort } = useClientSort(items, 'numero', 'desc');

  const buscar = () => {
    setPage(1);
    setTermoEmUso(termo.trim());
  };

  const baixarPdf = async (transferenciaId, numero) => {
    try {
      const { data } = await getTransferenciaRomaneioPdf(transferenciaId);
      const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `romaneio-${numero || transferenciaId}.pdf`.replace(/\//g, '-');
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Falha ao baixar PDF.'));
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Transferências de estoque"
        subtitle="Romaneios entre filiais — separação na origem e recebimento no destino."
        actions={
          <>
            <Tooltip title="Recarregar">
              <IconButton onClick={carregar} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {podeCriar && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/stock-transfers/new')}
              >
                Nova transferência
              </Button>
            )}
          </>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Buscar romaneio"
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && buscar()}
            size="small"
            fullWidth
          />
          <TextField
            select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            size="small"
            sx={{ minWidth: 180 }}
          >
            {STATUS_FILTRO.map((opt) => (
              <MenuItem key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="outlined" onClick={buscar}>
            Filtrar
          </Button>
        </Stack>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableTh id="numero" label="Romaneio" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="estabelecimentoOrigemNome" label="Origem" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="estabelecimentoDestinoNome" label="Destino" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="status" label="Status" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="totalItens" label="Itens" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="criadoEm" label="Criado em" orderBy={orderBy} order={order} onSort={requestSort} />
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Nenhuma transferência encontrada.
                </TableCell>
              </TableRow>
            ) : (
              itemsOrdenados.map((row) => {
                const st = statusTransferencia(row.status);
                return (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <strong>{row.numero}</strong>
                    </TableCell>
                    <TableCell>
                      {row.estabelecimentoOrigemNome}
                      {row.localEstoqueOrigemNome ? ` · ${row.localEstoqueOrigemNome}` : ''}
                    </TableCell>
                    <TableCell>
                      {row.estabelecimentoDestinoNome}
                      {row.localEstoqueDestinoNome ? ` · ${row.localEstoqueDestinoNome}` : ''}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={st.label} color={st.color} />
                    </TableCell>
                    <TableCell>{row.totalItens}</TableCell>
                    <TableCell>{formatDateTimeLocale(row.criadoEm)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Abrir">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/stock-transfers/${row.id}`)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="PDF do romaneio">
                          <IconButton
                            size="small"
                            onClick={() => baixarPdf(row.id, row.numero)}
                          >
                            <PictureAsPdfIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </ResponsiveTable>

      {totalPages > 1 && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
          <Box sx={{ mt: 1, typography: 'caption', color: 'text.secondary' }}>
            {totalItems} registro(s)
          </Box>
        </Stack>
      )}
    </Box>
  );
}
